<!-- src/components/layout/MobileNavigation.vue -->
<template>
  <q-footer elevated class="bg-white mobile-navigation">
    <!-- Main Navigation -->    <q-tabs
      v-model="currentTab"
      indicator-color="primary"
      class="text-primary"
      active-color="primary"
      dense
      no-caps
      align="justify"
      @update:model-value="handleTabChange"
    >      <q-tab name="home" to="/" exact label="Home" icon="home" @click="navigateTo('/')" />
      <q-tab name="stores" to="/stores" label="Stores" icon="store" @click="navigateTo('/stores')" />
      <q-tab name="coupons" to="/coupons" label="Coupons" icon="local_offer" @click="navigateTo('/coupons')" /><q-tab name="categories" to="/categories" label="Categories" icon="category" @click="navigateTo('/categories')" />      <q-tab name="menu" icon="more_horiz" @click="toggleMenu" />
    </q-tabs>

    <!-- Offline Indicator -->
    <div v-if="isOffline" class="offline-indicator">
      <q-icon name="cloud_off" size="xs" class="q-mr-xs" />
      You're offline. Some features may be limited.
    </div>    <!-- Update Available Indicator -->
    <div v-if="updateAvailable" class="update-indicator" @click="performUpdate">
      <q-icon name="system_update" size="xs" class="q-mr-xs" />
      Update available! Tap to refresh.
    </div>
    
    <!-- Menu Dialog -->
    <q-dialog v-model="menuDialog" position="bottom">
      <q-card class="menu-dialog">
        <q-card-section>
          <div class="text-h6">Menu</div>
        </q-card-section>
        
        <q-separator />
        
        <q-list padding>
          <q-item clickable v-close-popup @click="navigateTo('/featured')">
            <q-item-section avatar>
              <q-icon name="star" color="primary" />
            </q-item-section>
            <q-item-section>Featured Deals</q-item-section>
          </q-item>
          
          <q-item clickable v-close-popup @click="navigateTo('/about')">
            <q-item-section avatar>
              <q-icon name="info" color="primary" />
            </q-item-section>
            <q-item-section>About Us</q-item-section>
          </q-item>
            <q-item clickable v-close-popup @click="navigateTo('/contact-us')">
            <q-item-section avatar>
              <q-icon name="mail" color="primary" />
            </q-item-section>
            <q-item-section>Contact</q-item-section>
          </q-item>
          
          <q-item clickable v-close-popup @click="navigateTo('/privacy-policy')">
            <q-item-section avatar>
              <q-icon name="security" color="primary" />
            </q-item-section>
            <q-item-section>Privacy Policy</q-item-section>
          </q-item>
          
          <q-item clickable v-close-popup @click="navigateTo('/terms-of-service')">
            <q-item-section avatar>
              <q-icon name="gavel" color="primary" />
            </q-item-section>
            <q-item-section>Terms of Service</q-item-section>
          </q-item>
          
          <q-item clickable v-if="canInstall" v-close-popup @click="promptInstall">
            <q-item-section avatar>
              <q-icon name="get_app" color="primary" />
            </q-item-section>
            <q-item-section>Install App</q-item-section>
          </q-item>
        </q-list>
      </q-card>
    </q-dialog>
  </q-footer>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useResponsive } from '@/composables/useResponsive';
import { useOfflineHandler } from '@/composables/useOfflineHandler';

// Route information
const route = useRoute();
const router = useRouter();
const currentTab = ref('home');

// Get responsive helper
const { isMobile, isPwa } = useResponsive();
const offlineHandler = useOfflineHandler();

// PWA installation state
const canInstall = ref(false);
const deferredPrompt = ref(null);
const isOffline = ref(!navigator.onLine);
const updateAvailable = ref(false);
const menuDialog = ref(false);

// Set the current tab based on route
watch(() => route.path, (path) => {
  if (path === '/') currentTab.value = 'home';
  else if (path.startsWith('/stores')) currentTab.value = 'stores';
  else if (path.startsWith('/coupons')) currentTab.value = 'coupons';
  else if (path.startsWith('/category/') || path === '/categories') currentTab.value = 'categories';
  else if (path === '/featured' || path === '/about' || path === '/contact-us' || path === '/privacy-policy' || path === '/terms-of-service') currentTab.value = 'menu';
});

const updateOnlineStatus = () => {
  isOffline.value = !navigator.onLine;
};

// Handle PWA installation
const promptInstall = async () => {
  if (!deferredPrompt.value) return;
  
  // Show the install prompt
  deferredPrompt.value.prompt();
  
  // Wait for the user to respond to the prompt
  const { outcome } = await deferredPrompt.value.userChoice;
  console.log(`User ${outcome === 'accepted' ? 'accepted' : 'dismissed'} the install prompt`);
  
  // We can't use it again, so clear it
  deferredPrompt.value = null;
  canInstall.value = false;
};

// Handle PWA update
const performUpdate = () => {
  // Dispatch an event that App.vue listens for
  document.dispatchEvent(new CustomEvent('pwaUpdate'));
};

// Handle tab changes
const handleTabChange = (tabName) => {
  currentTab.value = tabName;
};

// Navigate to specific route
const navigateTo = (path) => {
  router.push(path);
};

// Toggle the menu dropdown
const toggleMenu = () => {
  // We'll use a q-dialog instead of menu for more reliable mobile behavior
  menuDialog.value = true;
};

// Lifecycle hooks
onMounted(() => {
  // Set initial tab
  if (route.path === '/') currentTab.value = 'home';
  else if (route.path.startsWith('/stores')) currentTab.value = 'stores';
  else if (route.path.startsWith('/coupons')) currentTab.value = 'coupons';
  else if (route.path.startsWith('/category/') || route.path === '/categories') currentTab.value = 'categories';
  else if (route.path === '/featured' || route.path === '/about' || route.path === '/contact-us' || route.path === '/privacy-policy' || route.path === '/terms-of-service') currentTab.value = 'menu';
  // Check online status
  window.addEventListener('online', updateOnlineStatus);
  window.addEventListener('offline', updateOnlineStatus);
  
  // Listen for PWA 'beforeinstallprompt' event
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent Chrome 76+ from automatically showing the prompt
    e.preventDefault();
    // Stash the event so it can be triggered later
    deferredPrompt.value = e;
    canInstall.value = true;
  });
  
  // Listen for service worker updates
  document.addEventListener('swUpdated', () => {
    updateAvailable.value = true;
  });
});

onUnmounted(() => {
  window.removeEventListener('online', updateOnlineStatus);
  window.removeEventListener('offline', updateOnlineStatus);
});
</script>

<style lang="scss" scoped>
.mobile-navigation {
  .q-tab {
    height: 56px;
  }
  
  .q-tab__content {
    flex-direction: column;
    
    .q-tab__icon {
      margin-right: 0;
      font-size: 24px;
      margin-bottom: 2px;
    }
    
    .q-tab__label {
      font-size: 12px;
    }
  }
}

.offline-indicator,
.update-indicator {
  padding: 4px 8px;
  font-size: 12px;
  text-align: center;
  color: white;
}

.offline-indicator {
  background-color: #f44336;
}

.update-indicator {
  background-color: #2196f3;
  cursor: pointer;
}

.menu-dialog {
  width: 100%;
  max-width: 350px;
  border-radius: 12px 12px 0 0;
}
</style>
